import { Stack, Typography } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-premium";
import { useGridApiRef, useKeepGroupedColumnsHidden } from "@mui/x-data-grid-premium";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import type { PortalRole } from "../../../../../../api/types/accessTypes";
import type { FundraisingAccessItem } from "../../../../../../api/types/fundraisingTypes";
import PortalRoleChip from "../../../../../common/PortalRoleChip";
import { ActionsCell, CategoriesCell, ContactCell } from "./fundraisingAccessGridProvider";

const groupingColumnDefinition: GridGroupingColDefOverride<FundraisingAccessItem> = {
  leafField: "contactName",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 1,
};

const orderedFundraisingPortalRoles: PortalRole[] = ["LP_Admin", "LP_Member"];

const columnDefinitions: GridColDef<FundraisingAccessItem>[] = [
  {
    field: "investorName",
    headerName: "",
    renderCell: ({ rowNode, field, value }) => {
      if (rowNode.type === "group" && field === rowNode.groupingField) {
        return "";
      }

      return (
        <Typography variant="subtitle2" noWrap>
          {value}
        </Typography>
      );
    },
  },
  {
    field: "contactName",
    headerName: "Name",
    cellClassName: "highlighted-action",
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return "";
      }

      return <ContactCell row={row} />;
    },
  },
  {
    field: "roles",
    headerName: "Role",
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return "";
      }

      const resolvedRole = orderedFundraisingPortalRoles.find((role) => row.roles.includes(role)) ?? "None";
      return <PortalRoleChip role={resolvedRole} />;
    },
  },
  {
    field: "categories",
    headerName: "Access Categories",
    minWidth: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return "";
      }

      return <CategoriesCell row={row} />;
    },
  },
  {
    field: "registrationDate",
    headerName: "Registration Status",
    width: 160,
    renderCell: ({ row, rowNode }) => {
      if (rowNode.type === "group") {
        return "";
      }

      return row.registrationDate ? (
        <Stack spacing={0.5} sx={{ width: "100%" }}>
          <Typography>Registered</Typography>
          <Typography variant="caption" noWrap color="textSecondary">
            {convertISODate(row.registrationDate)}
          </Typography>
        </Stack>
      ) : (
        <Typography>Not Registered</Typography>
      );
    },
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 80,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row, rowNode }) => (rowNode.type === "group" ? null : <ActionsCell row={row} />),
  },
];

interface Props {
  rows: FundraisingAccessItem[];
  isLoading: boolean;
}

const getRowId = (row: FundraisingAccessItem) => `${row.investorId}_${row.contactId}`;

const FundraisingAccessGrid = ({ rows, isLoading }: Props) => {
  const gridApiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(gridApiRef);

  const gridInitialState = useKeepGroupedColumnsHidden({
    apiRef: gridApiRef,
    initialState: {
      rowGrouping: {
        model: ["investorName"],
      },
      columns: {
        columnVisibilityModel: {
          contactName: false,
        },
      },
    },
  });

  return (
    <DataGrid<FundraisingAccessItem>
      apiRef={gridApiRef}
      columns={columnDefinitions}
      groupingColDef={groupingColumnDefinition}
      rows={rows}
      getRowId={getRowId}
      multilineRows
      loading={isLoading}
      initialState={gridInitialState}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No users"
      disableColumnPinning
      disableColumnSorting
      disableColumnReorder
      disableMultipleRowSelection
      containerSx={{ minHeight: 320 }}
    />
  );
};

export default FundraisingAccessGrid;
